import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { Add as AddIcon, DeleteOutline as DeleteOutlineIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareDatePicker from '../common/SalovareDatePicker';
import WaitlistTimePreferenceFields from './WaitlistTimePreferenceFields';
import WaitlistService from '../../services/waitlistService';
import {
  WAITLIST_PRESET_ANY,
  isStoreClosedOnDate,
  toDateInputValue,
} from '../../utils/waitlistWindowUtils';
import { parseCalendarDateKey } from '../../utils/dateFormatUtils';
import { fieldLabelRowSx } from '../common/formFieldStyles';
import { BRAND } from '../../theme/brandTokens';

const emptyRow = (date) => ({
  date: date || '',
  preset: WAITLIST_PRESET_ANY,
  from: '',
  to: '',
});

const toPreferencePayload = (row) => {
  if (!row.date) return null;
  if (row.preset === WAITLIST_PRESET_ANY && !row.from && !row.to) {
    return { date: row.date, windowKey: WAITLIST_PRESET_ANY, from: null, to: null };
  }
  if (row.from && row.to) {
    return {
      date: row.date,
      windowKey: row.preset || null,
      from: row.from,
      to: row.to,
    };
  }
  return null;
};

const BookingWaitlistDialog = ({
  open,
  onClose,
  storeId,
  openingHours,
  authorizationHours,
  specialOpeningDays,
  teamMemberWorkingHours,
  selectedDate,
  serviceIds,
  teamMemberIds,
  membershipPlanId,
  onJoined,
}) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([emptyRow(selectedDate)]);
  const [windowsByDate, setWindowsByDate] = useState({});
  const [saving, setSaving] = useState(false);
  const [errorKey, setErrorKey] = useState('');

  useEffect(() => {
    if (!open) return;
    setRows([emptyRow(selectedDate)]);
    setWindowsByDate({});
    setErrorKey('');
  }, [open, selectedDate]);

  const datesToLoad = useMemo(
    () => [...new Set(rows.map((row) => row.date).filter(Boolean))],
    [rows]
  );

  useEffect(() => {
    if (!open || !storeId) return undefined;
    let cancelled = false;

    const load = async () => {
      const missing = datesToLoad.filter((date) => !windowsByDate[date]);
      if (missing.length === 0) return;
      try {
        const results = await Promise.all(
          missing.map(async (date) => {
            const windows = await WaitlistService.getWindows(date, storeId);
            return [date, Array.isArray(windows) ? windows : []];
          })
        );
        if (cancelled) return;
        setWindowsByDate((prev) => ({
          ...prev,
          ...Object.fromEntries(results),
        }));
      } catch (error) {
        console.error('Failed to load waitlist windows', error);
        if (cancelled) return;
        setWindowsByDate((prev) => ({
          ...prev,
          ...Object.fromEntries(missing.map((date) => [date, prev[date] || []])),
        }));
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [datesToLoad, open, storeId, windowsByDate]);

  const updateRow = (index, patch) => {
    setRows((prev) => prev.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  };

  const handleSave = async () => {
    const preferences = rows.map(toPreferencePayload).filter(Boolean);
    if (preferences.length === 0) {
      setErrorKey('customer.appointments.booking.waitlist.error.preferences');
      return;
    }
    if (!serviceIds?.length) {
      setErrorKey('customer.appointments.booking.waitlist.error.services');
      return;
    }

    try {
      setSaving(true);
      setErrorKey('');
      await WaitlistService.join({
        serviceIds,
        teamMemberIds: teamMemberIds || [],
        membershipPlanId: membershipPlanId || null,
        preferences,
      }, storeId);
      onJoined?.();
      onClose?.();
    } catch (error) {
      console.error('Failed to join waitlist', error);
      setErrorKey('customer.appointments.booking.waitlist.error.save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      showCloseButton
      hideCloseOnXs
      dividers
      title={t('customer.appointments.booking.waitlist.title')}
      subtitle={t('customer.appointments.booking.waitlist.subtitle')}
      titleSx={{
        pb: 1,
        flexShrink: 0,
        gap: { xs: 0, sm: 2 },
        '& .MuiTypography-h5': { fontSize: { xs: '1.25rem', sm: '1.5rem' } },
      }}
      paperSx={{
        m: { xs: 2, sm: 4 },
        width: { xs: 'calc(100% - 32px)', sm: undefined },
        maxHeight: { xs: 'calc(100dvh - 32px)', sm: 'calc(100vh - 64px)' },
      }}
      contentSx={{
        maxHeight: { xs: 'none', sm: '70vh' },
        flex: '1 1 auto',
        minHeight: 0,
      }}
      actionsSx={{ flexShrink: 0, justifyContent: 'space-between', flexWrap: 'nowrap', gap: 1 }}
      actions={(
        <>
          <Button color="inherit" onClick={onClose} disabled={saving} sx={{ flexShrink: 0 }}>
            {t('common.cancel')}
          </Button>
          <PrimaryActionButton onClick={handleSave} disabled={saving} sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
            {saving ? t('common.saving') : t('customer.appointments.booking.waitlist.join')}
          </PrimaryActionButton>
        </>
      )}
    >
      <Stack spacing={2.5} sx={{ minWidth: 0 }}>
        {rows.map((row, index) => {
          const windows = windowsByDate[row.date] || [];
          return (
            <Box
              key={`waitlist-row-${index}`}
              sx={{
                display: 'grid',
                gap: 1.5,
                minWidth: 0,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ ...fieldLabelRowSx, justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    {t('customer.appointments.booking.selectDate')}
                  </Typography>
                  <IconButton
                    aria-label={t('customer.appointments.booking.waitlist.removeRow')}
                    onClick={() => setRows((prev) => (prev.length === 1 ? [emptyRow(selectedDate)] : prev.filter((_, rowIndex) => rowIndex !== index)))}
                    sx={{ width: 40, height: 40, mr: -1, mt: -0.5 }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Box>
                <SalovareDatePicker
                  value={parseCalendarDateKey(row.date)}
                  disablePast
                  shouldDisableDate={(date) => isStoreClosedOnDate(
                    toDateInputValue(date),
                    openingHours,
                    authorizationHours,
                    specialOpeningDays,
                    teamMemberIds,
                    teamMemberWorkingHours,
                  )}
                  onChange={(next) => updateRow(index, emptyRow(toDateInputValue(next)))}
                  slotProps={{
                    textField: {
                      inputProps: {
                        'aria-label': t('customer.appointments.booking.selectDate'),
                      },
                      sx: {
                        minWidth: 0,
                        width: '100%',
                        '& .MuiPickersInputBase-sectionContent': { fontSize: '16px' },
                      },
                    },
                  }}
                />
              </Box>
              <WaitlistTimePreferenceFields
                windows={windows}
                preset={row.preset}
                from={row.from}
                to={row.to}
                disabled={!row.date}
                onChange={(patch) => updateRow(index, patch)}
              />
            </Box>
          );
        })}
        <Button
          startIcon={<AddIcon />}
          onClick={() => setRows((prev) => [...prev, emptyRow(selectedDate)])}
          sx={{ alignSelf: 'flex-start', color: BRAND.accentText, fontWeight: 600, px: { xs: 0, sm: 1 } }}
        >
          {t('customer.appointments.booking.waitlist.addRow')}
        </Button>
        {errorKey ? (
          <Typography color="error" variant="body2">{t(errorKey)}</Typography>
        ) : null}
      </Stack>
    </ScrollableDialog>
  );
};

export default BookingWaitlistDialog;
